import { minuteS } from "../../../timeConstants.js";
import { SensorConversions } from "../conversionFunctions/sensorConversions.js";
import { SensorParameterConfigBase } from "../SensorConfigurationTypes.js";
import { frequencyOffsetParam, kFactorParam } from "./flowmeterSensorElement.js";
import { SensorElementConfig } from "./types.js";

const bidirectionalFlowmeterParams = {
    // Identical to flowmeter's kFactor — same physical parameter, no override needed.
    kFactor: kFactorParam,
    // Same shape as flowmeter's frequencyOffset, but with a description specific to what this
    // offset means for a bidirectional sensor.
    frequencyOffset: {
        ...frequencyOffsetParam,
        description:
            "Offset added to the raw pulse frequency before applying the K-factor (Hz). For a bidirectional " +
            "flowmeter this is typically the negative of the sensor's zero-flow frequency, so that frequencies " +
            "below it convert to negative (reverse) flow.",
    },
} as const satisfies Record<string, SensorParameterConfigBase<number>>;

export const bidirectionalFlowmeterElementConfig: SensorElementConfig<
    typeof bidirectionalFlowmeterParams,
    typeof SensorConversions.linearWithMinAndMax
> = {
    legalConvertedRange: [null, null],
    conversionKey: "linearWithMinAndMax",
    parameterConfigs: bidirectionalFlowmeterParams,
    buildConversionParams: ({ kFactor, frequencyOffset }) => ({
        slope: kFactor * minuteS,
        offset: frequencyOffset * kFactor * minuteS,
        // Unlike `flowmeter`, deliberately unclamped: this is the whole point of the element. The
        // sensor measures reverse flow below its zero-flow frequency, and clamping at 0 would throw
        // that away.
        outputMin: -Number.MAX_VALUE,
        outputMax: Number.MAX_VALUE,
    }),
    decodedUnits: "hertz",
    convertedUnits: "liters_per_minute",
    unitCategory: "liquidFlow",
    testCases: [
        {
            // Sensirion SLF3S-1300F (legacy SensorConfiguration.ts): 500 Hz is the zero-flow point;
            // result = 0.13 * freq / 1000 - 0.065, i.e. kFactor = 0.13/1000/60, frequencyOffset = -500.
            // Full reverse flow at 0 Hz.
            //
            // Note: the legacy conversion also forces a ±0.1 Hz deadband around 500 Hz to exactly 0,
            // which this linear form does not reproduce. The largest resulting disagreement is
            // ~6.5e-6 L/min at 499.95/500.05 Hz (bounded by 1.3e-5 L/min at the deadband edges) —
            // roughly 1e-5 of the sensor's 1300 mL/min full scale, well below its noise floor. See
            // sensorClusterMigration.test.ts for the cluster-level comparison against the legacy
            // conversion that measures this directly.
            parameters: { kFactor: 0.13 / 1000 / 60, frequencyOffset: -500 },
            decodedValue: 0,
            convertedValue: -0.065,
        },
        {
            // Same calibration, reverse flow partway to the zero point.
            parameters: { kFactor: 0.13 / 1000 / 60, frequencyOffset: -500 },
            decodedValue: 250,
            convertedValue: -0.0325,
        },
        {
            // Same calibration, at the zero-flow frequency. Unlike `flowmeter`'s threshold case,
            // this is genuinely invertible — nothing is clamped here — so the reverse check applies.
            parameters: { kFactor: 0.13 / 1000 / 60, frequencyOffset: -500 },
            decodedValue: 500,
            convertedValue: 0,
        },
        {
            // Same calibration, forward flow above the zero point.
            parameters: { kFactor: 0.13 / 1000 / 60, frequencyOffset: -500 },
            decodedValue: 1000,
            convertedValue: 0.065,
        },
        {
            // Different calibration, at a magnitude the default toBeCloseTo precision resolves
            // tightly: kFactor=1 L/pulse, frequencyOffset=-10 Hz → slope=60, offset=-600.
            // 5 Hz → -5 L/min.
            parameters: { kFactor: 1 / 60, frequencyOffset: -10 },
            decodedValue: 5,
            convertedValue: -5,
        },
    ],
} as const;
